import { FaPause, FaPlay } from "react-icons/fa";

import { Icons } from "@/components/icons/icons";
import { VideoMiniSlider } from "@/components/ui/videoMiniSlider";
import { FileText, Film, Highlighter, Webcam } from "lucide-react";
import { cn } from "@/lib/utils";
import { durationMsToShortString } from "@/helpers/helpers";
import { motion } from "framer-motion";
import { useDataContext } from "../../app/_context";
import { useEffect } from "react";

const HighlightBrick = ({
  isWithinSegment,
  segment,
  segmentIndex,
  sliderValue,
  kind = "talk",
  highlightIndex,
  playsHighlightsStream,
}: {
  segmentIndex: number;
  /** "realtime": research the recorder showed at normal speed; "external": a
   * named part of an uploaded video. Both are listed with the highlights but
   * are not among them (see HighlightsPlaylist). */
  kind?: "talk" | "realtime" | "external";
  /** True when the player is on the session's highlights-only stream. */
  playsHighlightsStream: boolean;
  /** Position among the session's highlights; null for shown work. */
  highlightIndex?: number | null;
  isWithinSegment: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  segment: any;
  sliderValue: number;
}) => {
  const {
    setSkipToTime,
    isHighlightsOnlyMode,
    currentSegment,
    setCurrentSegmentIndex,
    setCurrentSegment,
    setIsPaused,
    isPaused,
    setCurrentHighlightIndex,
    setIsSeeking,
    isPlayerWaiting,
  } = useDataContext();

  useEffect(() => {
    if (isWithinSegment) {
      setIsSeeking(false);
    }
  }, [isWithinSegment, setIsSeeking]);

  const isActiveAndSeeking =
    isPlayerWaiting && currentSegment?.id === segment.id;
  const shownWork = kind === "realtime";
  const notHighlight = kind !== "talk";
  // Highlights-only playback is a separate stream holding only highlights, so
  // anything else has nowhere to seek to there. A session without that stream
  // plays in full in either mode.
  const unavailable = notHighlight && playsHighlightsStream;
  const isActive = notHighlight ? isWithinSegment : currentSegment?.id === segment.id;
  const iconTone = isActive ? "text-accent" : "dark:text-[#FFB12D] text-[#b78021]";

  return (
    <motion.div
      key={`${segmentIndex ?? "1"}_highlight_link_${segment.id ?? "1"}`}
      initial={{ opacity: 0, y: -5, x: 0 * segmentIndex }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={{ opacity: 0, y: 5, x: -5 * segmentIndex }}
      className="w-full px-2"
      transition={{
        opacity: { duration: 0.1 },
        y: { duration: 0.1, ease: "easeInOut" },
        x: { duration: 0.2, ease: "easeInOut" },
        delay: segmentIndex * 0.3,
      }}
    >
      <button
        disabled={unavailable}
        title={unavailable ? `${segment.title} — plays in Full research` : undefined}
        onClick={() => {
          if (unavailable) return;
          if (notHighlight) {
            // A seek only: this isn't a highlight, so the highlight position
            // the skip controls follow is left alone.
            if (isWithinSegment) setIsPaused(!isPaused);
            else {
              setIsSeeking(true);
              setSkipToTime(segment.start);
            }
            return;
          }
          if (isWithinSegment) {
            setIsPaused(!isPaused);
          } else {
            setIsSeeking(true);
            setSkipToTime(
              isHighlightsOnlyMode
                ? segment.highlightsOnlyStart !== 0
                  ? segment.highlightsOnlyStart
                  : 0.0001
                : segment.start,
            );
          }
          setCurrentSegment(segment);
          setCurrentSegmentIndex(highlightIndex ?? segmentIndex);
          setCurrentHighlightIndex(highlightIndex ?? segmentIndex);
        }}
        className={cn(
          "border-2 rounded-md pl-1 border-transparent dark:bg-zinc-950 bg-white/10 mb-2 hover:bg-purple-500/5 relative w-full hover:opacity-85 opacity-90",
          isActive &&
            "border-accent opacity-100 dark:bg-zinc-800 bg-purple-500/10",
          unavailable && "cursor-default opacity-40 hover:opacity-40 hover:bg-transparent",
        )}
      >
        <span className="pointer-events-none absolute left-[5px] z-20 flex items-center top-1/2 -translate-y-1/2 rounded-tr-sm gap-1 text-xs p-1 rounded-sm">
          {kind === "external" ? (
            <Film size={13} className={iconTone} aria-label="Uploaded video" />
          ) : shownWork ? (
            <span className="relative inline-flex" aria-label="Shown work">
              <FileText size={13} className={iconTone} />
              <Highlighter
                size={8}
                strokeWidth={2.75}
                className={cn("absolute -right-1 -bottom-1", iconTone)}
              />
            </span>
          ) : (
            <Webcam size={13} className={iconTone} aria-label="Highlight" />
          )}
        </span>
        <div className="boundingBox p-2 w-full relative">
          <div className=" border-b dark:border-zinc-600 border-zinc-300 -bottom-px left-0 right-1 absolute" />
          <div className="flex items-center w-full">
            <div className="flex-grow overflow-hidden">
              <span className="block pl-4 text-xs truncate text-left">
                {segment.title}
              </span>
            </div>

            <div className="ml-4 flex-shrink-0 text-xs dark:text-gray-200">
              {isHighlightsOnlyMode && !notHighlight
                ? segment.highlightsOnlyStart > 1000
                  ? durationMsToShortString(segment.highlightsOnlyStart)
                  : "0:00"
                : segment.start > 1000
                ? durationMsToShortString(segment.start)
                : "0:00"}
              {isWithinSegment && (
                <VideoMiniSlider
                  key={segment.id}
                  max={segment.length}
                  value={[sliderValue]}
                  className="absolute pointer-events-none -left-1 -right-1 -bottom-1"
                  step={100}
                />
              )}
            </div>
            <PlayPauseSpin
              isActiveAndSeeking={isActiveAndSeeking}
              isWithinSegment={isWithinSegment}
              isPaused={isPaused}
            />
          </div>
        </div>
      </button>
    </motion.div>
  );
};

const PlayPauseSpin = ({
  isActiveAndSeeking,
  isWithinSegment,
  isPaused,
}: {
  isActiveAndSeeking: boolean;
  isWithinSegment: boolean;
  isPaused: boolean;
}) => {
  return (
    <div className="ml-2 flex-shrink-0">
      {isActiveAndSeeking ? (
        <Icons.spinner className="w-3 h-3 animate-spin text-accent" />
      ) : isWithinSegment && isPaused ? (
        <FaPause size={13} className={"text-accent"} />
      ) : (
        <FaPlay size={13} className={cn(isWithinSegment && "text-accent")} />
      )}
    </div>
  );
};

export default HighlightBrick;
